import React, { useRef, useEffect } from "react";
import { MdDelete, MdEdit } from "react-icons/md";
import { deleteIntervening } from "../../../Api";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

function InterveningTable({
  data = [],
  startIndex = 0,
  handleEditClick,
  handleDeleteClick,
  selectedRows = [],
  handleSelectRow,
  sortableFields = [],
  handleSort,
  renderSortIcon,
  handleSelectAll,
  isAllSelected = false,
  isSomeSelected = false,
}) {
  const navigate = useNavigate();

  const headerCheckboxRef = useRef(null);

  // Determine selection state for this page
  const currentPageIds = data.map((row) => row.record_id);

  const selectedOnThisPage = currentPageIds.filter((id) =>
    selectedRows.includes(id)
  );

  const allSelected =
    data.length > 0 && selectedOnThisPage.length === data.length;

  const someSelected =
    selectedOnThisPage.length > 0 && selectedOnThisPage.length < data.length;

  useEffect(() => {
    if (!headerCheckboxRef.current) return;
    headerCheckboxRef.current.indeterminate = false;
  }, [isAllSelected, isSomeSelected, someSelected, allSelected]);

  const handleDelete = async (recordId) => {
    try {
      const requestData = {
        record_id: recordId,
        correl_id: localStorage.getItem("correl_id") || "",
      };
      const result = await Swal.fire({
        text: `Are you sure you want to delete?`,
        icon: "warning",
        confirmButtonText: "Yes",
        showCancelButton: true,
        cancelButtonText: "No",
      });

      if (result.isConfirmed) {
        Swal.fire({
          html: `<img src=${require("../../../assets/loader.gif")} style="width: 50px" alt="loader">`,
          showConfirmButton: false,
          heightAuto: false,
          customClass: { popup: "loader-swal" },
          allowOutsideClick: false,
        });
        const apiData = await deleteIntervening(requestData);
        Swal.close();
        if (apiData.status === "S" && apiData.result_code === 200) {
          Swal.fire({
            text: "Intervening deleted successfully",
            icon: "success",
            showConfirmButton: false,
            timer: 3000,
          }).then(() => {
            handleDeleteClick();
          });
        } else if (
          apiData.status === "F" &&
          (apiData.message === "Unauthorized - Missing token" ||
            apiData.message === "Unauthorized - Invalid token")
        ) {
          Swal.fire({
            text: "Please login with your credentials",
            icon: "warning",
            showConfirmButton: false,
            customClass: {
              container: "swal-over-modal",
            },
            timer: 3000,
          }).then(() => {
            localStorage.clear();
            navigate("/login");
          });
        } else {
          Swal.fire({
            text: apiData.message || "Failed to delete intervening",
            icon: "warning",
            showConfirmButton: false,
            customClass: {
              container: "swal-over-modal",
            },
            timer: 3000,
          });
        }
      }
    } catch (error) {
      console.error("Error deleting intervening:", error);
      Swal.fire({
        text: "An error occurred while deleting the intervening",
        icon: "error",
        showConfirmButton: false,
        customClass: {
          container: "swal-over-modal",
        },
        timer: 3000,
      });
    }
  };

  // If data is not an array, show "No data available"
  if (!Array.isArray(data)) {
    return (
      <div className="table-responsive mb-3">
        <table className="table table-bordered table-striped">
          <thead className="table-dark">
            <tr>
              {/* Select-all checkbox in header */}
              <th>
                <input
                  type="checkbox"
                  ref={headerCheckboxRef}
                  checked={isAllSelected}
                  onChange={() => {
                    const shouldSelect = isSomeSelected ? false : !isAllSelected;
                    handleSelectAll(shouldSelect);
                  }}
                />
              </th>
              <th>#</th>
              <th>Intervening Words</th>
              <th>Created By</th>
              <th className="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="6" className="text-center">
                No data available
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="table-responsive mb-3">
      <table className="table table-bordered table-striped">
        <thead className="table-dark">
          <tr>
            {/* header for checkbox column */}
            <th style={{ width: "40px" }}>
              <input
                type="checkbox"
                ref={headerCheckboxRef}
                checked={isAllSelected}
                onChange={() => {
                  const shouldSelect = isSomeSelected ? false : !isAllSelected;
                  handleSelectAll(shouldSelect);
                }}
              />
            </th>
            <th>#</th>
            <th
              onClick={() =>
                handleSort && handleSort("INTERVENING_WORDS", sortableFields)
              }
              style={{
                cursor: sortableFields.includes("INTERVENING_WORDS")
                  ? "pointer"
                  : "default",
                userSelect: "none",
                position: "relative",
              }}
            >
              Intervening Words
              {renderSortIcon &&
                renderSortIcon("INTERVENING_WORDS", sortableFields)}
            </th>

            <th>Created By</th>
            {!allSelected && <th className="text-center">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan="6" className="text-center">
                No data available
              </td>
            </tr>
          ) : (
            data.map((item, index) => (
              <tr key={item.record_id || index}>
                {/* Row-level checkbox */}
                <td className="text-center">
                  <input
                    type="checkbox"
                    // guard access just in case record_id is undefined
                    checked={Boolean(
                      item.record_id && selectedRows.includes(item.record_id)
                    )}
                    onChange={() =>
                      handleSelectRow && handleSelectRow(item.record_id)
                    }
                  />
                </td>

                <td>{startIndex + index + 1}</td>
                <td>{item.word_list || ""}</td>
                <td>{item.created_by || ""}</td>
                {!allSelected && (
                  <td className="text-center">
                    <div
                      style={{
                        // Check if the current row (row.record_id) is selected
                        // If it is selected, hide the buttons. If not, show them.
                        visibility: selectedRows.includes(item.record_id)
                          ? "hidden"
                          : "visible",
                      }}
                    >
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => handleEditClick(item)}
                      >
                        <MdEdit />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(item.record_id)}
                      >
                        <MdDelete />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default InterveningTable;
